import { withinRadius, type LonLat } from "@arabom/geo";

export const ALERT_TARGETS = ["아동", "지적·자폐성·정신장애인", "치매환자", "성인"] as const;
export type AlertTarget = (typeof ALERT_TARGETS)[number];
export type AlertSettings = { radiusKm: number; targets: AlertTarget[]; timeWindows: { day: boolean; night: boolean }; region: string };
export const DEFAULT_ALERTS: AlertSettings = { radiusKm: 3, targets: [...ALERT_TARGETS], timeWindows: { day: true, night: false }, region: "서울" };

export function normalizeAlertSettings(value: unknown): AlertSettings {
  if (!value || typeof value !== "object") return structuredClone(DEFAULT_ALERTS);
  const raw = value as Partial<AlertSettings> & { daytimeOnly?: boolean };
  const radiusKm = Number.isFinite(Number(raw.radiusKm)) ? Math.min(20, Math.max(1, Math.round(Number(raw.radiusKm)))) : DEFAULT_ALERTS.radiusKm;
  const targets = Array.isArray(raw.targets) ? raw.targets.filter((item): item is AlertTarget => ALERT_TARGETS.includes(item as AlertTarget)) : [...ALERT_TARGETS];
  const timeWindows = raw.timeWindows && typeof raw.timeWindows === "object"
    ? { day: Boolean(raw.timeWindows.day), night: Boolean(raw.timeWindows.night) }
    : { day: raw.daytimeOnly !== false, night: raw.daytimeOnly === false };
  return { radiusKm, targets, timeWindows, region: typeof raw.region === "string" ? raw.region : "" };
}

export function validateAlertSettings(settings: AlertSettings): string[] {
  const errors: string[] = [];
  if (!settings.region.trim()) errors.push("알림을 받을 기준 위치가 필요해요. 지도에서 지역을 골라 주세요.");
  if (settings.targets.length === 0) errors.push("적어도 하나는 골라 주세요.");
  if (!settings.timeWindows.day && !settings.timeWindows.night) errors.push("받을 시간대를 골라 주세요. 둘 다 끄면 알림이 오지 않아요.");
  return errors;
}

export function estimateMonthly(settings: AlertSettings, publicCount: number) {
  const ratio = Math.min(1, settings.radiusKm / 20) * (settings.targets.length / ALERT_TARGETS.length);
  return Math.max(0, Math.round((publicCount / 12) * ratio));
}

export type NotificationSubscription = {
  center: LonLat;
  radiusKm: number;
  targetTypes: AlertTarget[];
  quietHours: { startHour: number; endHour: number };
  sentCaseIds: ReadonlySet<string>;
  sentInLastHour: number;
  sentToday: number;
};

export type NotificationCase = {
  caseId: string;
  targetType: AlertTarget;
  coords: LonLat | null;
};

export type NotificationDecision = {
  notify: boolean;
  queued: boolean;
  reason: "eligible" | "outside_radius" | "target_mismatch" | "quiet_hours" | "duplicate" | "hourly_limit" | "daily_limit";
};

function kstHour(now: Date): number {
  const hour = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    hour12: false,
    timeZone: "Asia/Seoul",
  }).formatToParts(now).find((part) => part.type === "hour")?.value;
  return Number(hour) % 24;
}

function isQuietHour(hour: number, quietHours: NotificationSubscription["quietHours"]): boolean {
  const start = quietHours.startHour;
  const end = quietHours.endHour;
  if (start === end) return false;
  return start < end ? hour >= start && hour < end : hour >= start || hour < end;
}

export function evaluateNotification(
  sub: NotificationSubscription,
  caseItem: NotificationCase,
  now: Date,
): NotificationDecision {
  if (!sub.targetTypes.includes(caseItem.targetType)) return { notify: false, queued: false, reason: "target_mismatch" };
  if (!withinRadius(sub.center, caseItem.coords, sub.radiusKm)) return { notify: false, queued: false, reason: "outside_radius" };
  if (sub.sentCaseIds.has(caseItem.caseId)) return { notify: false, queued: false, reason: "duplicate" };
  if (isQuietHour(kstHour(now), sub.quietHours)) return { notify: false, queued: true, reason: "quiet_hours" };
  if (sub.sentInLastHour >= 3) return { notify: false, queued: true, reason: "hourly_limit" };
  if (sub.sentToday >= 10) return { notify: false, queued: true, reason: "daily_limit" };
  return { notify: true, queued: false, reason: "eligible" };
}

export function shouldNotify(sub: NotificationSubscription, caseItem: NotificationCase, nowKst: Date): boolean {
  return evaluateNotification(sub, caseItem, nowKst).notify;
}
